import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, filter, Observable, of, startWith, Subscription, switchMap, tap } from 'rxjs';
import {
  MeterDto,
  MeteringPointDto,
  MeteringPointMeteredDataSourceService,
  MeteringPointService,
  MeterService,
  Quadrant
} from '@app/api/dmc';
import { FormApiService } from '@app/core/services/form-api.service';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { dateLessThanEqualValidator } from '@shared/validators/date-less-than-equal.validator';
import { getRegisterForQuadrant } from '@shared/modals/meteringpoint/source/meter-register';
import { isoToRealDate } from '@app/core/utils/iso-to-real-date';
import { MeterOptionLabelPipe } from '@shared/pipes/meter-option-label.pipe';

/**
 * Makes a meter quadrant the metering point's time-series source from the chosen date.
 * Offers the meters mounted on the connection point's meter frames on that date.
 */
@Component({
  selector: 'app-add-meter-source-modal',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    InputGroupComponent,
    LabelDirective,
    InputDirective,
    InputDateDirective,
    SelectDirective,
    MeterOptionLabelPipe,
  ],
  templateUrl: './add-meter-source-modal.component.html',
})
export class AddMeterSourceModalComponent implements OnInit, OnDestroy, HasUnsavedChanges {
  private readonly formApiService = inject(FormApiService);
  private readonly sourceService = inject(MeteringPointMeteredDataSourceService);
  private readonly meteringPointService = inject(MeteringPointService);
  private readonly meterService = inject(MeterService);
  private readonly subscriptions = new Subscription();

  /** Set by ModalService. Either the metering point or a row carrying its identification. */
  public meteringPoint?: MeteringPointDto;
  public element?: { meteringPointIdentification?: string };

  public readonly form = new FormGroup({
    validFrom: new FormControl<string | null>(isoToRealDate(new Date()), [Validators.required, dateLessThanEqualValidator(0)]),
    meterId: new FormControl<string | null>(null, Validators.required),
    serialNumber: new FormControl<string | null>({ value: null, disabled: true }),
    quadrant: new FormControl<Quadrant | null>(null, Validators.required),
  });

  public meterList: MeterDto[] = [];
  public readonly maxValidFrom = isoToRealDate(new Date());
  public readonly quadrants: KeyValue[] = enumToKeyValue(Quadrant, { omitKeys: ['NotSpecified'], prefixValue: 'enum.quadrant.' });

  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public get selectedMeter(): MeterDto | undefined {
    return this.meterList.find(meter => meter.id === this.form.controls.meterId.value);
  }

  public registerFor(quadrant: string): string | null {
    return this.selectedMeter ? getRegisterForQuadrant(this.selectedMeter, quadrant as Quadrant) : null;
  }

  public ngOnInit(): void {
    const validFrom = this.form.controls.validFrom;

    this.subscriptions.add(combineLatest([
      this.loadMeteringPoint(),
      validFrom.valueChanges.pipe(startWith(validFrom.value)),
    ]).pipe(
      filter(([meteringPoint, date]) => !!meteringPoint?.identification && !!date && validFrom.valid),
      switchMap(([meteringPoint, date]) => this.meterService.meterGetbymeteringpointGet(meteringPoint!.identification!, date!)),
    ).subscribe(meters => {
      this.meterList = meters;
      if (!this.selectedMeter) {
        this.form.controls.meterId.setValue(null);
      }
    }));

    this.subscriptions.add(this.form.controls.meterId.valueChanges.subscribe(() => {
      this.form.controls.serialNumber.setValue(this.selectedMeter?.serialNumber ?? null);
    }));
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public submit(): void {
    this.formApiService.submit<string>(
      this.form,
      data => this.sourceService.meteringPointMeteredDataSourceMeterPost(data),
      {
        preprocess: () => ({
          meteringPointIdentification: this.meteringPoint?.identification,
          validFrom: this.form.controls.validFrom.value,
          meterId: this.form.controls.meterId.value,
          quadrant: this.form.controls.quadrant.value,
        }),
        successMessage: 'meteringpoint.source.addmeter.success',
        closeModal: true,
      }
    );
  }

  private loadMeteringPoint(): Observable<MeteringPointDto | undefined> {
    const identification = this.element?.meteringPointIdentification;
    const meteringPoint$ = this.meteringPoint || !identification
      ? of(this.meteringPoint)
      : this.meteringPointService.meteringPointGetByIdentificationGet(identification);

    return meteringPoint$.pipe(tap(meteringPoint => this.meteringPoint = meteringPoint));
  }
}
